import {format, parseISO} from 'date-fns';
import {CellValue} from '../data/reportRowModel';
import {ReportGrain} from '../reportGrain';
import {humanizeToken} from '../tokenLabel';

/** A calendar day with no time on it. */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const DATE_FORMAT = 'd MMM yyyy';
const DATE_TIME_FORMAT = 'd MMM yyyy, HH:mm';

/** The fields a formatter needs to know about, which is the subset of the taxonomy it reads. */
interface DisplayField {
	key: string;
	dataType?: string;
	values?: string[];
}

/**
 * Formats an ISO date or datetime, keeping the time when there is one.
 *
 * The time is not decoration on an audit log: one batch action writes many rows inside the same
 * second, so a reader working through a sequence needs it. A value that does not parse is returned
 * untouched rather than shown as "Invalid date".
 */
function formatDateString(value: string): string {
	const trimmed = value.trim();
	if (trimmed === '') {
		return value;
	}
	try {
		const parsed = parseISO(trimmed);
		if (Number.isNaN(parsed.getTime())) {
			return value;
		}
		return format(parsed, ISO_DATE_ONLY.test(trimmed) ? DATE_FORMAT : DATE_TIME_FORMAT);
	} catch {
		return value;
	}
}

export type CellFormatter = (path: string, value: CellValue | undefined) => CellValue | undefined;

/** Leaves a cell exactly as the mapper produced it. Used where no taxonomy has resolved yet. */
export const identityCellFormatter: CellFormatter = (_path, value) => value;

/**
 * Formats cells for display, driven by what the grain's taxonomy says each field is.
 *
 * Two things were reaching the screen raw. A field with a fixed vocabulary rendered its wire token
 * (`STATUS_CHANGE`), and a date-typed field rendered its ISO string (`2026-08-27T09:12:00Z`) —
 * both correct as data and both unreadable as a column. The taxonomy already says which fields are
 * which, so this reads it rather than keeping a second list in step by hand.
 *
 * Formatting happens at render only. Sorting and filtering keep working on the underlying value, so
 * a column still orders by its real date rather than alphabetically by its formatted label.
 */
/** The grains whose cells this formats. Deliberately not every grain: the task and project-status
 * columns that publish a vocabulary already have their own wording - RAG and Stage go through
 * `formatLabel` in the mapper, and Budget type renders the raw wire token ON PURPOSE, so that the
 * value in the column and the value in the condition picker read the same. Formatting those here
 * would break that agreement and restate two label maps that already exist. The audit grain has no
 * such maps, and its tokens and timestamps are what this exists for. */
const FORMATTED_GRAINS: ReadonlySet<ReportGrain> = new Set<ReportGrain>(['TIME_REG_AUDIT_LOG']);

export function createCellFormatter(
	taxonomy: {fields?: DisplayField[]} | null | undefined,
	grain: ReportGrain | null
): CellFormatter {
	// Required, not optional. While it could be omitted the default was "format everything", so a
	// caller who simply did not pass it would silently reformat the Budget type and RAG tokens the
	// comment above says must stay raw. There is no live bug -- the one caller passes `rowGrainOf()`
	// -- and making it required is what keeps that true.
	if (grain === null || !FORMATTED_GRAINS.has(grain)) {
		return identityCellFormatter;
	}
	const vocabularyPaths = new Set<string>();
	const datePaths = new Set<string>();

	for (const field of taxonomy?.fields ?? []) {
		if (field.values && field.values.length > 0) {
			vocabularyPaths.add(field.key);
		}
		if (field.dataType === 'date') {
			datePaths.add(field.key);
		}
	}

	if (vocabularyPaths.size === 0 && datePaths.size === 0) {
		return identityCellFormatter;
	}

	return (path, value) => {
		if (!value) {
			return value;
		}
		if (value.kind === 'date') {
			return {kind: 'date', value: formatDateString(value.value)};
		}
		if (value.kind !== 'text') {
			return value;
		}
		if (vocabularyPaths.has(path)) {
			return {kind: 'text', value: humanizeToken(value.value)};
		}
		if (datePaths.has(path)) {
			return {kind: 'text', value: formatDateString(value.value)};
		}
		return value;
	};
}
